<div class="exceedance-compliance-widget">
  @if (widgetTitle) {
    <div class="widget-title-header">
      <div class="widget-title-content">
        <h4 class="widget-title">{{ titleMeta.isCustom ? widgetTitle : (widgetTitle | translate) }}{{ titleMeta.tailPrefix }}{{ titleMeta.tailKey | translate }}{{ titleMeta.tail }}</h4>
        <i class="material-icons info-icon"
          [matTooltip]="'Includes approved samples in the selected time period and organization whose lab results match the applied filters (agent / agent group, jurisdiction and OEL type). Blank samples and results without a calculated %OEL are excluded.' | translate"
        matTooltipPosition="above">info_outline</i>
      </div>
      @if (vm?.resolvedDateRange) {
        <span class="resolved-date-range">{{ vm.resolvedDateRange }}</span>
      }
    </div>
  }
  @if (widgetTitle) {
    <hr class="widget-divider">
  }

  <div class="filters-wrapper">
    <app-exceedance-compliance-filters
      mode="inline"
      [values]="filterValues"
      [effectiveOrgId]="effectiveOrgIdForFilters"
      (filterChange)="onFilterChange($event)">
      <span filterAppend class="site-filter-tooltip-wrap"
        [matTooltip]="'Site is locked while a jurisdiction filter is applied.' | translate"
        [matTooltipDisabled]="!isSiteSelectorDisabled">
        <app-widget-org-filter
          [orgLevelId]="orgLevelOverrideId"
          [disabled]="isSiteSelectorDisabled"
          (orgLevelIdChange)="onOrgLevelOverrideChange($event)">
        </app-widget-org-filter>
      </span>
    </app-exceedance-compliance-filters>
  </div>

  <div class="widget-content">
    @if (isLoading) {
      <div class="loading-container">
        <div class="loading-spinner"></div>
      </div>
    }

    @if (error && !isLoading) {
      <div class="error-container">
        <i class="material-icons">error_outline</i>
        <span>{{ error }}</span>
      </div>
    }

    @if (!isLoading && !error && data && vm) {
      <div class="kpi-row">
        <div class="kpi-card kpi-compliance" [class.kpi-not-evaluable]="!vm.isComplianceRateEvaluable">
          @if (vm.isComplianceRateEvaluable) {
            <div class="kpi-value">{{ data.complianceRate }}%</div>
            <div class="kpi-label">{{ 'Compliance Rate' | translate }}</div>
            <div class="kpi-trend" [ngClass]="vm.complianceRate.trendClass">
              @if (vm.complianceRate.showTrendIcon) {
                <i class="material-icons trend-icon">{{ data.complianceRateTrend | trendIcon }}</i>
              }
              <span>{{ vm.complianceRate.trendLabel }}</span>
            </div>
          }
          @if (!vm.isComplianceRateEvaluable) {
            <div class="kpi-value">{{ 'Not evaluable' | translate }}</div>
            <div class="kpi-label">{{ 'Compliance Rate' | translate }}</div>
            <div class="kpi-reason">{{ 'No samples with a measured concentration in this period' | translate }}</div>
          }
        </div>
        <div class="kpi-card kpi-exceedances">
          <div class="kpi-value">{{ data.totalExceedances }}</div>
          <div class="kpi-label">{{ 'Exceedances' | translate }}</div>
          <div class="kpi-trend" [ngClass]="vm.totalExceedances.trendClass">
            @if (vm.totalExceedances.showTrendIcon) {
              <i class="material-icons trend-icon">{{ data.totalExceedancesTrend | trendIcon }}</i>
            }
            <span>{{ vm.totalExceedances.trendLabel }}</span>
          </div>
        </div>
        <div class="kpi-card kpi-samples">
          <div class="kpi-value">{{ data.totalSamples }}</div>
          <div class="kpi-label">{{ 'Total Samples' | translate }}</div>
          <div class="kpi-trend" [ngClass]="vm.totalSamples.trendClass">
            <span>{{ vm.totalSamples.trendLabel }}</span>
          </div>
        </div>
        <div class="kpi-card kpi-segs">
          <div class="kpi-value">{{ data.segsWithExceedances }}</div>
          <div class="kpi-label">{{ 'SEGs with Exceedances' | translate }}</div>
          <div class="kpi-trend" [ngClass]="vm.segsWithExceedances.trendClass">
            @if (vm.segsWithExceedances.showTrendIcon) {
              <i class="material-icons trend-icon">{{ data.segsWithExceedancesTrend | trendIcon }}</i>
            }
            <span>{{ vm.segsWithExceedances.trendLabel }}</span>
          </div>
        </div>
        <div class="kpi-card kpi-total-segs">
          <div class="kpi-value">{{ data.totalSegsSampled }}</div>
          <div class="kpi-label">{{ 'Total SEGs Sampled' | translate }}</div>
        </div>
      </div>
      <div class="agent-table-container">
        <div class="section-label">{{ 'Exceedances by Agent' | translate }}</div>
        @if (sortedAgents.length > 0) {
          <table class="agent-table">
            <thead>
              <tr>
                @for (header of tableHeaders; track trackByIndex($index)) {
                  <th
                    class="sortable" [class.text-center]="header.centered"
                    (click)="sortBy(header.key)">
                    {{ header.label | translate }}
                    @if (sort.column === header.key) {
                      <i class="material-icons sort-icon">{{ sort.ascending ? 'arrow_upward' : 'arrow_downward' }}</i>
                    }
                  </th>
                }
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (agent of sortedAgents; track trackByAgentId($index, agent)) {
                <tr class="clickable-row" tabindex="0"
                  role="button" (click)="onAgentRowClick(agent)" (keydown.enter)="onAgentRowClick(agent)"
                  (keydown.space)="onAgentRowClick(agent); $event.preventDefault()">
                  <td class="agent-name"><strong>{{ agent.agentName }}</strong></td>
                  <td class="text-center">{{ agent.samples }}</td>
                  <td class="text-center exceedance-count" [class.has-exceedances]="agent.exceedances > 0">
                    {{ agent.exceedances }}
                  </td>
                  <td class="text-center">
                    <span class="rate-badge" [ngClass]="agent.badgeClass">
                      {{ agent.exceedanceRate }}%
                    </span>
                  </td>
                  <td class="text-center vs-prior" [ngClass]="agent.trendClass">
                    @if (agent.exceedanceRateTrend != null && agent.exceedanceRateTrend !== 0) {
                      <i class="material-icons trend-icon-sm">{{ agent.exceedanceRateTrend | trendIcon }}</i>
                      {{ agent.trendDisplay }}
                      @if (vm.resolvedPriorDateRange) {
                        <span class="prior-dates">({{ vm.resolvedPriorDateRange }})</span>
                      }
                    }
                    @if (agent.exceedanceRateTrend == null || agent.exceedanceRateTrend === 0) {
                      — 0%
                    }
                  </td>
                  <td class="text-center highest-result">
                    @if (agent.highestResultDisplay) {
                      {{ agent.highestResultDisplay }}
                      @if (agent.highestOelMultiple) {
                        <span class="oel-multiple">({{
                        agent.highestOelMultiple }})</span>
                      }
                    }
                    @if (!agent.highestResultDisplay) {
                      —
                    }
                  </td>
                  <td class="drill-arrow">→</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
      @if (sortedAgents.length === 0) {
        <div class="no-data">
          <i class="material-icons">check_circle</i>
          <span>{{ 'No exceedances found for the selected filters' | translate }}</span>
        </div>
      }
    }
  </div>

  @if (!isLoading && !error && data && vm && sortedAgents.length > 0) {
    <div class="click-hint">
      {{ 'Click any row to open sample grid with filter pre-applied' | translate }} →
    </div>
  }
  @if (!isLoading && !error && data) {
    <button class="view-all-link" (click)="viewFullReport()">
      {{ 'View Full Exceedance Report' | translate }} →
    </button>
  }
</div>
